import { useMemo } from 'react';

import {
  Ai,
  BilateralCornerArrows,
  BoardAdd,
  BoardDuplicate,
  Boards,
  Download,
  Finder,
  Link,
  Move,
  Pencil,
  Slack,
  Star,
  StarFilled,
  Trash,
} from '@air/next-icons';
import { type DropdownMenuOption } from '@air/primitive-dropdown-menu';
import { useAirModal } from '@air/provider-modal';
import { type Board } from '@air/types';

import { displaySlackChannelName } from '~/components/EditPrivateBoardModal/EditBoardModal/SlackIntegration/utils/displaySlackChannelName';
import { useEditBoard } from '~/components/EditPrivateBoardModal/hooks/useEditBoard';
import { useBoardTemplateModal } from '~/components/Modals/BoardTemplateModal/useBoardTemplateModal';
import { useDeleteBoardsModal } from '~/components/Modals/DeleteBoards/useDeleteBoardsModal';
import { useDuplicateBoard } from '~/components/Modals/DuplicateBoard/hooks/useDuplicateBoard';
import { useMoveItemsModal } from '~/components/Modals/MoveItemsModal/useMoveItemsModal';
import { SharePrivateBoardModal } from '~/components/SharePrivateBoardModal/SharePrivateBoardModal';
import { useExcludedIntegrationsFlag } from '~/hooks/flags/useExcludedIntegrationsFlag';
import { useBoardPermissions } from '~/hooks/useBoardPermissions';
import { useCanShowInFinderOption } from '~/hooks/useCanShowInFinderOption';
import { useCreateSubBoard } from '~/hooks/useCreateSubBoard';
import { useDownloadPrivateBoard } from '~/hooks/useDownloadPrivateBoard';
import { useGoToBoardPage } from '~/hooks/useGoToBoardPage';
import { useDesktopAppConnectionContext } from '~/providers/DesktopAppConnectionProvider';
import { useGetBoardContainerPermissions } from '~/swr-hooks/boards/useGetBoardContainerPermissions';
import { useToggleFavoritedBoard } from '~/swr-hooks/boards/useToggleFavoritedBoard';
import { useSlackMenuOptions } from '~/utils/menuOptions/useSlackMenuOptions';
import {
  canChangeBoardDetails,
  canCreateBoard,
  canDeleteBoard,
  canDuplicateBoard,
} from '~/utils/permissions/boardPermissions';
import { canViewShortUrl } from '~/utils/permissions/shortUrlPermissions';
import { canGenerateZip } from '~/utils/permissions/taskPermissions';

const TRACK_LOCATION = 'side-nav-tree-item';

export type UseWorkspaceSidebarBoardDropdownOptionsParams = {
  board: Board;
  open: boolean | undefined;
  onRename: () => void;
};

export const useWorkspaceSidebarBoardDropdownOptions = ({
  board,
  open,
  onRename,
}: UseWorkspaceSidebarBoardDropdownOptionsParams) => {
  const { getBoardContainerPermissions } = useGetBoardContainerPermissions();
  const { boardPermissions } = useBoardPermissions({
    boardId: board.id,
  });
  const { goToBoardPage } = useGoToBoardPage();
  const { downloadPrivateBoard } = useDownloadPrivateBoard();
  const { canShowOpenInFinder } = useCanShowInFinderOption();
  const { openBoardInFinder } = useDesktopAppConnectionContext();
  const { createSubBoard } = useCreateSubBoard();
  const { toggleFavoritedBoard } = useToggleFavoritedBoard();
  const { showDuplicateBoardsModal } = useDuplicateBoard();
  const { showEditBoardModal } = useEditBoard();
  const { showMoveItemsModal } = useMoveItemsModal();
  const { onConnectToSlack, onConnectedToSlack, slackIntegrationData } = useSlackMenuOptions(open ? board : undefined);
  const { excludedIntegrations } = useExcludedIntegrationsFlag();
  const { showDeleteBoardsModal } = useDeleteBoardsModal();
  const [showSharePrivateBoardModal] = useAirModal(SharePrivateBoardModal);
  const { showBoardTemplateModal } = useBoardTemplateModal();

  const boardContainerPermissions = getBoardContainerPermissions({ board });

  const workspaceSidebarBoardDropdownOptions = useMemo(() => {
    const workspaceSidebarBoardDropdownOptionsArray: DropdownMenuOption[] = [];

    workspaceSidebarBoardDropdownOptionsArray.push({
      id: 'open',
      label: 'Open',
      onSelect: () => goToBoardPage({ board }),
      prefix: <BilateralCornerArrows className="size-4" />,
      type: 'item',
    });

    if (canGenerateZip(boardPermissions)) {
      workspaceSidebarBoardDropdownOptionsArray.push({
        id: 'download',
        label: 'Download',
        onSelect: () =>
          downloadPrivateBoard({
            boardId: board.id,
            trackLocation: TRACK_LOCATION,
          }),
        prefix: <Download className="size-4" />,
        type: 'item',
      });
    }

    if (canShowOpenInFinder && canGenerateZip(boardPermissions)) {
      workspaceSidebarBoardDropdownOptionsArray.push({
        id: 'open-in-finder',
        label: 'Open in Finder',
        onSelect: () => openBoardInFinder(board.id),
        prefix: <Finder className="size-4" />,
        type: 'item',
      });
    }

    workspaceSidebarBoardDropdownOptionsArray.push({
      id: 'group-1-separator',
      type: 'separator',
    });

    if (canViewShortUrl(boardPermissions)) {
      workspaceSidebarBoardDropdownOptionsArray.push({
        id: 'share-link',
        label: 'Share link',
        onSelect: () => showSharePrivateBoardModal({ boardId: board.id, trackLocation: TRACK_LOCATION }),
        prefix: <Link className="size-4" />,
        type: 'item',
      });
    }

    if (canCreateBoard(boardPermissions)) {
      workspaceSidebarBoardDropdownOptionsArray.push({
        id: 'add-sub-board',
        label: 'Add sub-board',
        onSelect: () =>
          createSubBoard({
            selectedParentBoard: board,
            trackLocation: TRACK_LOCATION,
          }),
        prefix: <BoardAdd className="size-4" />,
        type: 'item',
      });

      workspaceSidebarBoardDropdownOptionsArray.push({
        id: 'generate-board-structure',
        label: 'Board templates',
        onSelect: () =>
          showBoardTemplateModal({
            workspaceId: board.workspaceId,
            parentBoardId: board.id,
          }),
        prefix: <Ai className="size-4" />,
        type: 'item',
      });
    }

    if (!board.hasCurrentUser) {
      workspaceSidebarBoardDropdownOptionsArray.push({
        id: 'favorite',
        label: 'Favorite',
        onSelect: () => toggleFavoritedBoard({ board, trackLocation: TRACK_LOCATION }),
        prefix: <Star className="size-4" />,
        type: 'item',
      });
    } else {
      workspaceSidebarBoardDropdownOptionsArray.push({
        id: 'unfavorite',
        label: 'Unfavorite',
        onSelect: () => toggleFavoritedBoard({ board, trackLocation: TRACK_LOCATION }),
        prefix: <StarFilled className="size-4" />,
        type: 'item',
      });
    }

    workspaceSidebarBoardDropdownOptionsArray.push({
      id: 'group-2-separator',
      type: 'separator',
    });

    if (canChangeBoardDetails(boardPermissions)) {
      workspaceSidebarBoardDropdownOptionsArray.push({
        id: 'rename',
        label: 'Rename',
        onSelect: onRename,
        prefix: <Pencil className="size-4" />,
        type: 'item',
      });
    }

    if (
      canDuplicateBoard({
        boardContext: boardPermissions,
        targetContext: boardContainerPermissions,
      })
    ) {
      workspaceSidebarBoardDropdownOptionsArray.push({
        id: 'duplicate',
        label: 'Duplicate',
        onSelect: () => showDuplicateBoardsModal({ boards: [board], trackLocation: TRACK_LOCATION }),
        prefix: <BoardDuplicate className="size-4" />,
        type: 'item',
      });
    }

    if (canChangeBoardDetails(boardPermissions)) {
      workspaceSidebarBoardDropdownOptionsArray.push({
        id: 'edit-board-details',
        label: 'Edit board details',
        onSelect: () => showEditBoardModal({ board, trackLocation: TRACK_LOCATION }),
        prefix: <Boards className="size-4" />,
        type: 'item',
      });
    }

    if (canDeleteBoard(boardContainerPermissions)) {
      workspaceSidebarBoardDropdownOptionsArray.push({
        id: 'move-to',
        label: 'Move to…',
        onSelect: () => showMoveItemsModal({ boards: [board], trackLocation: TRACK_LOCATION }),
        prefix: <Move className="size-4" />,
        type: 'item',
      });
    }

    if (onConnectToSlack || onConnectedToSlack) {
      workspaceSidebarBoardDropdownOptionsArray.push({
        id: 'group-3-separator',
        type: 'separator',
      });

      if (!!onConnectToSlack && !excludedIntegrations.includes('slack')) {
        workspaceSidebarBoardDropdownOptionsArray.push({
          id: 'connect-to-slack',
          label: 'Connect to Slack',
          onSelect: () => onConnectToSlack(TRACK_LOCATION),
          prefix: <Slack className="size-4" />,
          type: 'item',
        });
      }

      if (!!onConnectedToSlack && slackIntegrationData) {
        workspaceSidebarBoardDropdownOptionsArray.push({
          id: 'connected-to-slack',
          label: 'Connected to Slack',
          description: displaySlackChannelName(slackIntegrationData.channelName),
          onSelect: () => onConnectedToSlack(TRACK_LOCATION),
          prefix: <Slack className="size-4" />,
          type: 'item',
        });
      }
    }

    if (canDeleteBoard(boardContainerPermissions)) {
      workspaceSidebarBoardDropdownOptionsArray.push({
        id: 'group-4-separator',
        type: 'separator',
      });

      workspaceSidebarBoardDropdownOptionsArray.push({
        id: 'delete',
        className: 'text-red-9',
        label: 'Delete from workspace',
        onSelect: () => showDeleteBoardsModal({ boardIds: [board.id], trackLocation: TRACK_LOCATION }),
        prefix: <Trash className="size-4" />,
        type: 'item',
      });
    }

    return workspaceSidebarBoardDropdownOptionsArray;
  }, [
    board,
    boardContainerPermissions,
    boardPermissions,
    canShowOpenInFinder,
    createSubBoard,
    downloadPrivateBoard,
    excludedIntegrations,
    goToBoardPage,
    onConnectToSlack,
    onConnectedToSlack,
    onRename,
    openBoardInFinder,
    showDeleteBoardsModal,
    showDuplicateBoardsModal,
    showEditBoardModal,
    showMoveItemsModal,
    showSharePrivateBoardModal,
    slackIntegrationData,
    toggleFavoritedBoard,
    showBoardTemplateModal,
  ]);

  return {
    workspaceSidebarBoardDropdownOptions,
  };
};
